import { create } from "zustand";
import {
  loadContacts,
  loadDeviceSession,
  loadMessages,
  saveDeviceSession,
  saveMessage,
  clearDeviceSession,
  upsertContact,
} from "../db";
import { mqttClient } from "../mqtt/client";
import {
  getOrCreateDeviceId,
  loadOperatorName,
  loadRememberedMqttUser,
  loadRememberedWsUrl,
  saveOperatorName,
  saveRememberedMqttUser,
  saveRememberedWsUrl,
} from "../lib/storage";
import {
  DEVICE_TOKEN_TTL_MS,
  PAIRING_CODE_TTL_MS,
  generatePairingCode,
  assistantMessageId,
  presenceFromLastSeen,
  type AriaContact,
  type ChatMessage,
  type ConnectionPhase,
  type DeliveryStatus,
  type DeviceSession,
  type MqttCredentials,
  type PairingState,
  type FleetEnvelope,
} from "../types";

type AppState = {
  connectionPhase: ConnectionPhase;
  pairing: PairingState;
  deviceSession: DeviceSession | null;
  mqttCreds: MqttCredentials | null;
  operatorName: string;
  contacts: AriaContact[];
  selectedAriaId: string | null;
  messages: ChatMessage[];
  mobileShowChat: boolean;
  error: string | null;
  devMode: boolean;

  init: () => Promise<void>;
  setOperatorName: (name: string) => void;
  connectMqtt: (creds: MqttCredentials, operatorName: string) => Promise<void>;
  startPairing: (ariaId: string) => Promise<void>;
  logout: () => Promise<void>;
  selectContact: (ariaId: string) => Promise<void>;
  backToRoster: () => void;
  sendMessage: (text: string) => Promise<void>;
  setDevMode: (on: boolean) => void;
  seedDemoData: () => Promise<void>;
};

function upsertContactFromEnvelope(
  contacts: AriaContact[],
  env: FleetEnvelope,
): AriaContact[] {
  const ariaId = env.agentId;
  const existing = contacts.find((c) => c.ariaId === ariaId);
  const base: AriaContact = existing ?? {
    ariaId,
    name: ariaId,
    labels: {},
    presence: "offline",
    unreadCount: 0,
  };

  if (env.type === "aria.announce") {
    const name = typeof env.payload.name === "string" ? env.payload.name : base.name;
    const hostname =
      typeof env.payload.hostname === "string" ? env.payload.hostname : base.hostname;
    const labels =
      env.payload.labels && typeof env.payload.labels === "object"
        ? (env.payload.labels as Record<string, string>)
        : base.labels;
    const updated: AriaContact = {
      ...base,
      name,
      hostname,
      labels,
      lastSeenAt: env.ts,
      presence: "online",
    };
    return mergeContact(contacts, updated);
  }

  if (env.type === "aria.status") {
    const lastSeenAt = typeof env.payload.lastSeenAt === "string" ? env.payload.lastSeenAt : env.ts;
    const warm = env.payload.warm === true;
    const updated: AriaContact = {
      ...base,
      name: typeof env.payload.name === "string" ? env.payload.name : base.name,
      lastSeenAt,
      warm,
      presence: presenceFromLastSeen(lastSeenAt),
      typing: env.payload.typing === true,
    };
    return mergeContact(contacts, updated);
  }

  return contacts;
}

function mergeContact(contacts: AriaContact[], contact: AriaContact): AriaContact[] {
  const rest = contacts.filter((c) => c.ariaId !== contact.ariaId);
  return [...rest, contact].sort((a, b) => {
    const at = a.lastMessage?.ts ?? a.lastSeenAt ?? "";
    const bt = b.lastMessage?.ts ?? b.lastSeenAt ?? "";
    return Date.parse(bt) - Date.parse(at);
  });
}

function handleOutboxMessage(
  get: () => AppState,
  set: (partial: Partial<AppState>) => void,
  env: FleetEnvelope,
): void {
  if (env.agentId !== get().selectedAriaId) return;

  if (env.type === "chat.delivered") {
    set({
      messages: get().messages.map((m) =>
        m.id === env.id && m.role === "user" ? { ...m, delivery: "delivered" } : m,
      ),
    });
    return;
  }

  if (env.type === "chat.read") {
    set({
      messages: get().messages.map((m) =>
        m.id === env.id && m.role === "user" ? { ...m, delivery: "read" } : m,
      ),
    });
    return;
  }

  if (env.type === "chat.typing") {
    set({
      contacts: get().contacts.map((c) =>
        c.ariaId === env.agentId ? { ...c, typing: env.payload.active === true } : c,
      ),
    });
    return;
  }

  if (env.type === "chat.chunk") {
    // Single set(): mark user read + append/update assistant bubble together.
    const text = typeof env.payload.text === "string" ? env.payload.text : "";
    if (!text) {
      set({
        messages: get().messages.map((m) =>
          m.id === env.id && m.role === "user" ? { ...m, delivery: "read" as const } : m,
        ),
      });
      return;
    }
    const aid = assistantMessageId(env.id);
    const messages = get().messages;
    let foundAssistant = false;
    const next = messages.map((m) => {
      if (m.id === env.id && m.role === "user") {
        return { ...m, delivery: "read" as const };
      }
      if (m.id === aid && m.role === "assistant") {
        foundAssistant = true;
        return { ...m, text: m.text + text, streaming: true };
      }
      return m;
    });
    if (!foundAssistant) {
      next.push({
        id: aid,
        turnId: env.id,
        ariaId: env.agentId,
        role: "assistant",
        text,
        ts: env.ts,
        streaming: true,
      });
    }
    set({ messages: next });
    return;
  }

  if (env.type === "chat.done") {
    const reply = typeof env.payload.reply === "string" ? env.payload.reply : "";
    const aid = assistantMessageId(env.id);
    const messages = get().messages;
    const existing = messages.find((m) => m.id === aid && m.role === "assistant");
    const finalMsg: ChatMessage = existing
      ? { ...existing, text: reply || existing.text, streaming: false }
      : {
          id: aid,
          turnId: env.id,
          ariaId: env.agentId,
          role: "assistant",
          text: reply,
          ts: env.ts,
        };
    void saveMessage(finalMsg);
    // Ensure user bubble stays read, never replaced by assistant id.
    const withUserRead = messages.map((m) =>
      m.id === env.id && m.role === "user" ? { ...m, delivery: "read" as const } : m,
    );
    const nextMessages = existing
      ? withUserRead.map((m) => (m.id === aid ? finalMsg : m))
      : [...withUserRead, finalMsg];

    const contacts = get().contacts;
    const updatedContacts = mergeContact(contacts, {
      ...(contacts.find((c) => c.ariaId === env.agentId) ?? {
        ariaId: env.agentId,
        name: env.agentId,
        labels: {},
        presence: "online",
        unreadCount: 0,
      }),
      lastMessage: { text: finalMsg.text.slice(0, 80), ts: finalMsg.ts, role: "assistant" },
      typing: false,
    });
    void upsertContact(updatedContacts.find((c) => c.ariaId === env.agentId)!);
    set({
      messages: nextMessages,
      contacts: updatedContacts,
    });
    return;
  }

  if (env.type === "console.paired") {
    const deviceToken =
      typeof env.payload.deviceToken === "string" ? env.payload.deviceToken : "";
    const expiresAt =
      typeof env.payload.expiresAt === "string"
        ? env.payload.expiresAt
        : new Date(Date.now() + DEVICE_TOKEN_TTL_MS).toISOString();
    if (!deviceToken) return;
    const session: DeviceSession = {
      deviceId: getOrCreateDeviceId(),
      deviceToken,
      operatorName: get().operatorName,
      pairedAt: new Date().toISOString(),
      expiresAt,
    };
    void saveDeviceSession(session);
    set({ deviceSession: session, pairing: { phase: "paired" } });
    return;
  }

  if (env.type === "chat.error") {
    const code = env.payload.code;
    set({
      error:
        code === "unauthorized"
          ? "Access denied — pairing may have expired"
          : String(env.payload.message ?? "Message failed"),
    });
  }

  if (env.type === "chat.cancelled") {
    const aid = assistantMessageId(env.id);
    set({
      messages: get().messages.map((m) =>
        m.id === aid ? { ...m, streaming: false } : m,
      ),
      contacts: get().contacts.map((c) =>
        c.ariaId === env.agentId ? { ...c, typing: false } : c,
      ),
    });
  }
}

export const useAppStore = create<AppState>((set, get) => ({
  connectionPhase: "disconnected",
  pairing: { phase: "idle" },
  deviceSession: null,
  mqttCreds: null,
  operatorName: loadOperatorName(),
  contacts: [],
  selectedAriaId: null,
  messages: [],
  mobileShowChat: false,
  error: null,
  devMode: import.meta.env.DEV,

  init: async () => {
    const [session, contacts] = await Promise.all([
      loadDeviceSession(),
      loadContacts(),
    ]);
    set({ deviceSession: session, contacts });

    mqttClient.setHandlers({
      onConnect: () => set({ connectionPhase: "connected", error: null }),
      onDisconnect: () => set({ connectionPhase: "disconnected" }),
      onError: (err) => set({ error: err.message }),
      onEnvelope: (topic, env) => {
        if (topic.includes("/registry/announce") || topic.includes("/status")) {
          const updated = upsertContactFromEnvelope(get().contacts, env);
          set({ contacts: updated });
          const c = updated.find((x) => x.ariaId === env.agentId);
          if (c) void upsertContact(c);
        }
        if (topic.includes("/web/out/")) {
          handleOutboxMessage(get, set, env);
        }
      },
    });

    if (session && get().mqttCreds) {
      try {
        set({ connectionPhase: "connecting" });
        await mqttClient.connect(get().mqttCreds!);
        set({ pairing: { phase: "paired" } });
      } catch {
        /* wait for manual reconnect */
      }
    }
  },

  setOperatorName: (name) => {
    saveOperatorName(name);
    set({ operatorName: name });
  },

  connectMqtt: async (creds, operatorName) => {
    saveRememberedWsUrl(creds.wsUrl);
    saveRememberedMqttUser(creds.username);
    saveOperatorName(operatorName);
    set({
      connectionPhase: "connecting",
      mqttCreds: creds,
      operatorName,
      error: null,
    });
    try {
      await mqttClient.connect(creds);
      const session = await loadDeviceSession();
      set({
        connectionPhase: "connected",
        deviceSession: session,
        pairing: session ? { phase: "paired" } : { phase: "idle" },
      });
    } catch (err) {
      set({
        connectionPhase: "disconnected",
        error: err instanceof Error ? err.message : "Connection failed",
      });
      throw err;
    }
  },

  startPairing: async (ariaId) => {
    const { operatorName, mqttCreds } = get();
    if (!mqttCreds) throw new Error("Connect to MQTT first");
    const code = generatePairingCode();
    const expiresAt = new Date(Date.now() + PAIRING_CODE_TTL_MS).toISOString();
    const deviceId = getOrCreateDeviceId();
    const label = `${operatorName || "Web"} · ${navigator.userAgent.includes("Mobile") ? "Mobile" : "Desktop"}`;
    set({ pairing: { phase: "awaiting_approval", code, expiresAt } });
    await mqttClient.publishPairRequest(ariaId, deviceId, code, label);
  },

  logout: async () => {
    await mqttClient.disconnect();
    await clearDeviceSession();
    set({
      connectionPhase: "disconnected",
      pairing: { phase: "idle" },
      deviceSession: null,
      mqttCreds: null,
      selectedAriaId: null,
      messages: [],
      mobileShowChat: false,
    });
  },

  selectContact: async (ariaId) => {
    const msgs = await loadMessages(ariaId);
    set({
      selectedAriaId: ariaId,
      messages: msgs,
      mobileShowChat: true,
      contacts: get().contacts.map((c) =>
        c.ariaId === ariaId ? { ...c, unreadCount: 0 } : c,
      ),
    });
  },

  backToRoster: () => set({ mobileShowChat: false }),

  sendMessage: async (text) => {
    const { selectedAriaId, deviceSession, operatorName, devMode, messages } = get();
    if (!selectedAriaId) return;

    const trimmed = text.trim();
    if (!trimmed) return;

    const msgId = crypto.randomUUID();
    const ts = new Date().toISOString();
    const userMsg: ChatMessage = {
      id: msgId,
      turnId: msgId,
      ariaId: selectedAriaId,
      role: "user",
      text: trimmed,
      ts,
      delivery: "pending",
    };

    const nextMessages = [...messages, userMsg];
    set({ messages: nextMessages });
    void saveMessage(userMsg);

    if (devMode && !deviceSession) {
      setTimeout(() => {
        set({
          messages: get().messages.map((m) =>
            m.id === msgId ? { ...m, delivery: "sent" as DeliveryStatus } : m,
          ),
        });
        setTimeout(() => {
          const reply: ChatMessage = {
            id: crypto.randomUUID(),
            ariaId: selectedAriaId,
            role: "assistant",
            text: `Demo reply to: "${trimmed}"`,
            ts: new Date().toISOString(),
          };
          set({ messages: [...get().messages, reply] });
          void saveMessage(reply);
        }, 600);
      }, 200);
      return;
    }

    if (!deviceSession) {
      set({ error: "Device not paired — approve pairing on AARIA TUI" });
      return;
    }

    try {
      await mqttClient.publishChat(
        selectedAriaId,
        msgId,
        trimmed,
        deviceSession.deviceToken,
        deviceSession.deviceId,
        operatorName,
      );
      set({
        messages: get().messages.map((m) =>
          m.id === msgId ? { ...m, delivery: "sent" } : m,
        ),
      });
    } catch (err) {
      set({
        messages: get().messages.map((m) =>
          m.id === msgId ? { ...m, delivery: "failed" } : m,
        ),
        error: err instanceof Error ? err.message : "Send failed",
      });
    }
  },

  setDevMode: (on) => set({ devMode: on }),

  seedDemoData: async () => {
    const demo: AriaContact[] = [
      {
        ariaId: "desk-home",
        name: "ARIA · home desk",
        hostname: "pop-os",
        labels: { env: "home" },
        presence: "online",
        lastSeenAt: new Date().toISOString(),
        warm: true,
        unreadCount: 0,
        lastMessage: {
          text: "Morning brief ready.",
          ts: new Date(Date.now() - 120_000).toISOString(),
          role: "assistant",
        },
      },
      {
        ariaId: "desk-vps",
        name: "ARIA · VPS",
        hostname: "contabo",
        labels: { env: "prod" },
        presence: "idle",
        lastSeenAt: new Date(Date.now() - 3_600_000).toISOString(),
        warm: true,
        unreadCount: 2,
        lastMessage: {
          text: "Fleet health OK.",
          ts: new Date(Date.now() - 86_400_000).toISOString(),
          role: "assistant",
        },
      },
    ];
    for (const c of demo) await upsertContact(c);
    set({ contacts: demo, connectionPhase: "connected", pairing: { phase: "paired" } });
  },
}));

export function getLoginDefaults() {
  return {
    wsUrl: loadRememberedWsUrl(),
    mqttUser: loadRememberedMqttUser(),
    operatorName: loadOperatorName(),
  };
}
